import { useState, useEffect, useMemo } from 'react';
import { Search, Edit, Check, X, RefreshCw, UserPlus, Shield, Users, ChevronLeft, ChevronRight, ChevronDown, ChevronRight as ChevronRightIcon, MoreVertical, Key, History, ShoppingCart, Clock, Filter, DollarSign } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useDebounce } from '../../hooks/useDebounce';
import { useNavigate } from 'react-router-dom';
import UserCartPreview from './users/UserCartPreview';
import { usePasswordReset } from './users/usePasswordReset';

interface User {
  id: string;
  name: string;
  email: string;
  company: string;
  role: 'customer' | 'admin';
  status: 'active' | 'pending' | 'suspended';
  verified: boolean;
  joinDate: string;
  phone?: string;
  admin_permissions?: AdminPermissionsForm;
  price_type?: 'contado' | 'credito' | 'mayorista' | 'publico';
}

interface AdminPermissionsForm {
  orders: boolean;
  products: boolean;
  catalogs: boolean;
  users: boolean;
  reports: boolean;
}

// Etiquetas en español para permisos
const permissionLabels: Record<keyof AdminPermissionsForm, string> = {
  orders: 'Pedidos',
  products: 'Productos',
  catalogs: 'Catalogos',
  users: 'Usuarios',
  reports: 'Dashboard'
};

const priceTypeLabels: Record<string, { label: string; color: string }> = {
  contado: { label: 'Contado', color: 'bg-green-100 text-green-800' },
  credito: { label: 'Credito', color: 'bg-blue-100 text-blue-800' },
  mayorista: { label: 'Mayorista', color: 'bg-purple-100 text-purple-800' },
  publico: { label: 'Publico', color: 'bg-gray-100 text-gray-800' }
};

export default function UsersTab() {
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showManageUserModal, setShowManageUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [adminPermissions, setAdminPermissions] = useState<AdminPermissionsForm>({
    orders: false,
    products: false,
    catalogs: false,
    users: false,
    reports: false
  });
  const [editPermissions, setEditPermissions] = useState<AdminPermissionsForm>({
    orders: false,
    products: false,
    catalogs: false,
    users: false,
    reports: false
  });
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [forceRefresh, setForceRefresh] = useState(0);
  const [activeManageTab, setActiveManageTab] = useState<'info' | 'permisos' | 'precios'>('info');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [activeActionsUserId, setActiveActionsUserId] = useState<string | null>(null);
  const { sendPasswordReset, loading: resetLoading } = usePasswordReset();

  // Estado para filtros avanzados
  const [priceTypeFilter, setPriceTypeFilter] = useState<string>('all');
  const [showNewUsersOnly, setShowNewUsersOnly] = useState(false);

  // Estado para paginacion
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Funcion para obtener usuarios de Supabase
  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('Error al obtener el usuario actual:', userError);
        throw new Error('No se pudo verificar tu sesion. Por favor, inicia sesion nuevamente.');
      }

      if (!user) {
        throw new Error('No se ha encontrado un usuario autenticado. Por favor, inicia sesion.');
      }

      setCurrentUserId(user.id);

      const { data: currentUserProfile, error: profileError } = await supabase
        .from('profiles')
        .select('id, name, company, phone, is_admin, admin_permissions, created_at, price_type')
        .eq('id', user.id)
        .single();

      if (profileError) {
        console.error('Error al obtener el perfil del usuario:', profileError);
        if (profileError.code === '42P17') {
          throw new Error('Se ha detectado un problema con las politicas de seguridad. Por favor, contacta al administrador del sistema.');
        } else {
          throw profileError;
        }
      }

      const isAdmin = currentUserProfile?.is_admin || false;
      const hasUserPermission = currentUserProfile?.admin_permissions?.users || false;

      if (!isAdmin && !hasUserPermission) {
        throw new Error('No tienes permisos para acceder a la gestion de usuarios.');
      }

      setIsCurrentUserAdmin(isAdmin);

      const { data: profilesWithEmail, error: profilesError } = await supabase
        .from('profiles')
        .select('id, name, company, phone, is_admin, admin_permissions, created_at, price_type, email');

      if (profilesError) {
        console.error('Error al obtener perfiles con email:', profilesError);

        const { data: usersWithEmail, error: rpcError } = await supabase
          .rpc('get_users_with_emails');

        if (rpcError) {
          console.error('Error al obtener usuarios con RPC:', rpcError);
          throw rpcError;
        }

        const formattedUsers = usersWithEmail.map((profile: any) => {
          return {
            id: profile.id,
            name: profile.name || 'Usuario',
            email: profile.email || 'Email no disponible',
            company: profile.company || 'N/A',
            role: profile.is_admin ? 'admin' as const : 'customer' as const,
            status: (profile.status as 'active' | 'pending' | 'suspended') || 'active',
            verified: true,
            joinDate: profile.created_at,
            phone: profile.phone,
            admin_permissions: profile.admin_permissions,
            price_type: profile.price_type as 'contado' | 'credito' | 'mayorista' | 'publico' | undefined
          };
        });

        setUsers(formattedUsers);
        setSelectedFilter('all');
        return;
      }

      const formattedUsers = profilesWithEmail.map((profile) => {
        return {
          id: profile.id,
          name: profile.name || 'Usuario',
          email: profile.email || 'Email no disponible',
          company: profile.company || 'N/A',
          role: profile.is_admin ? 'admin' as const : 'customer' as const,
          status: ((profile as any).status as 'active' | 'pending' | 'suspended') || 'active',
          verified: true,
          joinDate: profile.created_at,
          phone: profile.phone,
          admin_permissions: profile.admin_permissions,
          price_type: profile.price_type as 'contado' | 'credito' | 'mayorista' | 'publico' | undefined
        };
      });

      setUsers(formattedUsers);
      setSelectedFilter('all');
    } catch (error: any) {
      console.error('Error al obtener usuarios:', error);
      alert(`Error: ${error.message || 'Error desconocido al obtener usuarios'}`);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = useMemo(() => {
    // Calcular fecha de hace 7 dias para filtro de "nuevos"
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return users.filter(user => {
      // Filtro de busqueda por texto (nombre, email o empresa)
      if (debouncedSearchTerm &&
          !user.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) &&
          !user.email.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) &&
          !(user.company && user.company.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))) {
        return false;
      }

      // Filtro de usuarios nuevos (ultimos 7 dias)
      if (showNewUsersOnly) {
        const joinDate = new Date(user.joinDate);
        if (joinDate < sevenDaysAgo) return false;
      }

      // Filtro por tipo de precio
      if (priceTypeFilter !== 'all' && user.price_type !== priceTypeFilter) {
        return false;
      }

      // Filtros de estado/rol
      if (selectedFilter === 'active') return user.status === 'active';
      if (selectedFilter === 'pending') return user.status === 'pending';
      if (selectedFilter === 'verified') return user.verified;
      if (selectedFilter === 'admin') return user.role === 'admin';

      return true;
    });
  }, [users, selectedFilter, debouncedSearchTerm, priceTypeFilter, showNewUsersOnly]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredUsers, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, selectedFilter, priceTypeFilter, showNewUsersOnly]);

  useEffect(() => {
    fetchUsers();
    const timer = setTimeout(() => {
      setSelectedFilter('all');
    }, 500);
    return () => clearTimeout(timer);
  }, [forceRefresh]);

  const handleAddAdmin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    setIsLoading(true);

    try {
      const { error: requestError } = await supabase
        .from('admin_requests')
        .insert([{
          email: formData.get('email') as string,
          name: formData.get('name') as string,
          phone: formData.get('phone') as string,
          admin_permissions: {
            ...adminPermissions,
            catalogs: adminPermissions.catalogs,
          },
          price_type: formData.get('price_type') as 'contado' | 'credito' | 'mayorista' | 'publico',
          status: 'pending',
          requested_at: new Date().toISOString()
        }]);

      if (requestError) {
        if (requestError.code === '42P01') {
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseAnonKey =
            import.meta.env.VITE_SUPABASE_ANON_KEY ||
            import.meta.env.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY;

          const response = await fetch(`${supabaseUrl}/functions/v1/auth-signup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'apikey': supabaseAnonKey,
            },
            body: JSON.stringify({
              email: formData.get('email') as string,
              password: formData.get('password') as string,
              userData: {
                name: formData.get('name') as string,
                company: 'Disolgua',
                phone: formData.get('phone') as string,
                price_type: formData.get('price_type') as 'contado' | 'credito' | 'mayorista' | 'publico',
                is_admin: true,
                admin_permissions: {
                  ...adminPermissions,
                  catalogs: adminPermissions.catalogs,
                },
              },
              generateSession: false,
            }),
          });

          const result = await response.json();

          if (!response.ok || result.error) {
            throw new Error(result.error || 'Error al crear usuario');
          }

          const user = result.data?.user;
          if (!user) throw new Error('No user returned');

          const { error: profileError } = await supabase
            .from('profiles')
            .insert([{
              id: user.id,
              name: formData.get('name'),
              company: 'Disolgua',
              phone: formData.get('phone'),
              is_admin: true,
              admin_permissions: {
                ...adminPermissions,
                catalogs: adminPermissions.catalogs,
              },
              price_type: formData.get('price_type') as 'contado' | 'credito' | 'mayorista' | 'publico',
              status: 'pending'
            }]);

          if (profileError) throw profileError;
        } else {
          throw requestError;
        }
      }

      setShowAddAdminModal(false);
      alert('Solicitud de administrador enviada correctamente. Un administrador revisara tu solicitud.');
      fetchUsers();
    } catch (error) {
      console.error('Error adding admin:', error);
      alert('Error al anadir administrador. Por favor, intentalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserStatus = async (userId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: newStatus })
        .eq('id', userId);

      if (error) throw error;

      setUsers(users.map(user =>
        user.id === userId
          ? { ...user, status: newStatus as 'active' | 'pending' | 'suspended' }
          : user
      ));

      alert(`Estado del usuario cambiado a ${newStatus === 'active' ? 'Activo' : 'Suspendido'}`);
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Error al cambiar el estado del usuario');
    }
  };

  const handleEditUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const formData = new FormData(e.currentTarget);
      if (!selectedUser) throw new Error('No user selected');

      const updates = {
        name: formData.get('name') as string || selectedUser.name,
        company: formData.get('company') as string || selectedUser.company,
        phone: formData.get('phone') as string || selectedUser.phone,
        is_admin: formData.has('is_admin') ? formData.get('is_admin') === 'true' : selectedUser.role === 'admin',
        admin_permissions: editPermissions,
        price_type: (formData.get('price_type') as any) || selectedUser.price_type
      };

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', selectedUser.id);

      if (updateError) throw updateError;

      setShowManageUserModal(false);
      alert('Usuario actualizado correctamente');
      fetchUsers();
    } catch (error: any) {
      console.error('Error updating user:', error);
      alert(`Error: ${error.message || 'Error al actualizar usuario'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const openManageUserModal = (user: User) => {
    setSelectedUser(user);
    setEditPermissions({
      orders: user.admin_permissions?.orders ?? false,
      products: user.admin_permissions?.products ?? false,
      catalogs: user.admin_permissions?.catalogs ?? false,
      users: user.admin_permissions?.users ?? false,
      reports: user.admin_permissions?.reports ?? false
    });
    setShowManageUserModal(true);
    setActiveManageTab('info');
  };

  const handleAddCustomer = async () => {
    try {
      setIsLoading(true);

      const randomEmail = `cliente${Math.floor(Math.random() * 10000)}@example.com`;
      const randomPassword = `password${Math.floor(Math.random() * 100000)}`;

      const { data: { user }, error: authError } = await supabase.auth.signUp({
        email: randomEmail,
        password: randomPassword,
        options: {
          data: {
            name: `Cliente Real ${Math.floor(Math.random() * 1000)}`,
            company: 'Empresa Cliente Real',
            phone: '987654321'
          }
        }
      });

      if (authError) throw authError;
      if (!user) throw new Error('No se pudo crear el usuario');

      const { error: profileError } = await supabase
        .from('profiles')
        .insert([{
          id: user.id,
          name: `Cliente Real ${Math.floor(Math.random() * 1000)}`,
          company: 'Empresa Cliente Real',
          phone: '987654321',
          is_admin: false,
          admin_permissions: {
            orders: false,
            products: false,
            catalogs: false,
            users: false,
            reports: false
          },
          price_type: 'publico'
        }]);

      if (profileError) throw profileError;

      alert(`Se ha creado un nuevo usuario cliente real en la base de datos.\nEmail: ${randomEmail}\nPassword: ${randomPassword}\n\nGuarda estas credenciales porque no se mostraran de nuevo.`);

      fetchUsers();
    } catch (error: any) {
      console.error('Error al crear usuario cliente:', error);
      alert(`Error al crear usuario cliente: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendReset = async (user: User) => {
    setActiveActionsUserId(null);
    if (!confirm(`¿Enviar email de reset de contrasena a ${user.email}?`)) return;

    const success = await sendPasswordReset(user.email, user.name);
    if (success) {
      alert('Email de reset enviado correctamente');
    } else {
      alert('Error al enviar email de reset');
    }
  };

  const handleViewOrders = (userId: string) => {
    setActiveActionsUserId(null);
    navigate(`/admin?tab=orders&userId=${userId}`);
  };

  return (
    <div className="space-y-6">
      {/* Header con acciones */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm">
        <h1 className="text-2xl font-bold text-gray-900">Gestion de Usuarios</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setForceRefresh(prev => prev + 1);
              setSelectedFilter('all');
            }}
            className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="text-sm font-medium">Actualizar</span>
          </button>
        </div>
      </div>

      {/* Mensaje informativo para usuarios no administradores */}
      {users.length === 1 && !isCurrentUserAdmin && users[0].id === currentUserId && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Solo los administradores pueden ver todos los perfiles de usuario. Actualmente estas viendo solo tu perfil.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre, email o empresa..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: 'Todos', icon: Users },
              { id: 'active', label: 'Activos', icon: Check },
              { id: 'pending', label: 'Pendientes', icon: RefreshCw },
              { id: 'admin', label: 'Administradores', icon: Shield },
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setSelectedFilter(filter.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 ${selectedFilter === filter.id
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-500 hover:bg-gray-50'
                  }`}
              >
                <filter.icon className="w-4 h-4" />
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filtros avanzados */}
        <div className="flex flex-col sm:flex-row gap-4 mt-4 pt-4 border-t">
          {/* Filtro de usuarios nuevos */}
          <button
            onClick={() => setShowNewUsersOnly(!showNewUsersOnly)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              showNewUsersOnly
                ? 'bg-green-100 text-green-700 border border-green-300'
                : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Clock className="w-4 h-4" />
            Nuevos (7 dias)
          </button>

          {/* Filtro por lista de precios */}
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-500" />
            <span className="text-sm text-gray-600">Lista:</span>
            <select
              value={priceTypeFilter}
              onChange={(e) => setPriceTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todas</option>
              <option value="contado">Contado</option>
              <option value="credito">Credito</option>
              <option value="mayorista">Mayorista</option>
              <option value="publico">Publico</option>
            </select>
          </div>

          {/* Botón para limpiar filtros */}
          {(showNewUsersOnly || priceTypeFilter !== 'all' || selectedFilter !== 'all') && (
            <button
              onClick={() => {
                setShowNewUsersOnly(false);
                setPriceTypeFilter('all');
                setSelectedFilter('all');
              }}
              className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 text-red-600 hover:bg-red-50 transition-colors"
            >
              <X className="w-4 h-4" />
              Limpiar filtros
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-3 mt-4 justify-end">
          <button
            onClick={() => setShowAddAdminModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <UserPlus className="w-5 h-5" />
            <span>Anadir Admin</span>
          </button>
          <button
            onClick={handleAddCustomer}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
            disabled={isLoading}
          >
            <UserPlus className="w-5 h-5" />
            <span>{isLoading ? 'Creando...' : 'Anadir Cliente'}</span>
          </button>
        </div>
      </div>

      {/* Contenido principal - Tabla de usuarios */}
      <div className="space-y-4">
        {isLoading && users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl shadow-sm">
            <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mb-4" />
            <p className="text-gray-600">Cargando usuarios...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-xl shadow-sm">
            <Users className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-700 mb-2">No se encontraron usuarios</h3>
            <p className="text-gray-500 max-w-md text-center mb-4">
              {searchTerm
                ? `No hay usuarios que coincidan con "${searchTerm}"`
                : `No hay usuarios con el filtro seleccionado (${selectedFilter})`
              }
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                Limpiar busqueda
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 pl-1">
              Mostrando {paginatedUsers.length} de {filteredUsers.length} {filteredUsers.length === 1 ? 'usuario' : 'usuarios'}
              {totalPages > 1 && ` (Pagina ${currentPage} de ${totalPages})`}
            </p>

            {/* Tabla de usuarios */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-10 px-4 py-3"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Empresa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">Telefono</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Lista</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {paginatedUsers.map((user) => {
                      const isExpanded = expandedUserId === user.id;
                      const priceInfo = priceTypeLabels[user.price_type || 'publico'];
                      const showActions = activeActionsUserId === user.id;

                      return (
                        <>
                          {/* Fila principal */}
                          <tr
                            key={user.id}
                            className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}
                            onClick={() => setExpandedUserId(isExpanded ? null : user.id)}
                          >
                            {/* Expand icon */}
                            <td className="px-4 py-3">
                              {isExpanded ? (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                              )}
                            </td>

                            {/* Usuario */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-blue-600 font-medium">
                                    {user.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-gray-900 truncate">{user.name}</p>
                                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                                </div>
                              </div>
                            </td>

                            {/* Empresa */}
                            <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                              {user.company || 'N/A'}
                            </td>

                            {/* Telefono */}
                            <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                              {user.phone || 'N/A'}
                            </td>

                            {/* Tipo de precio */}
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${priceInfo.color}`}>
                                {priceInfo.label}
                              </span>
                            </td>

                            {/* Rol */}
                            <td className="px-4 py-3 hidden md:table-cell">
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                }`}>
                                {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                                {user.role === 'admin' ? 'Admin' : 'Cliente'}
                              </span>
                            </td>

                            {/* Estado */}
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.status === 'active' ? 'bg-green-100 text-green-800' :
                                  user.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                }`}>
                                {user.status === 'active' ? 'Activo' :
                                  user.status === 'pending' ? 'Pendiente' : 'Suspendido'}
                              </span>
                            </td>

                            {/* Acciones */}
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="relative">
                                <button
                                  onClick={() => setActiveActionsUserId(showActions ? null : user.id)}
                                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <MoreVertical className="w-5 h-5 text-gray-500" />
                                </button>

                                {showActions && (
                                  <>
                                    <div className="fixed inset-0 z-10" onClick={() => setActiveActionsUserId(null)} />
                                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border z-20">
                                      <button
                                        onClick={() => { openManageUserModal(user); setActiveActionsUserId(null); }}
                                        className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <Edit className="w-4 h-4" /> Editar
                                      </button>
                                      <button
                                        onClick={() => handleSendReset(user)}
                                        disabled={resetLoading}
                                        className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                                      >
                                        <Key className="w-4 h-4" />
                                        {resetLoading ? 'Enviando...' : 'Reset contrasena'}
                                      </button>
                                      <button
                                        onClick={() => handleViewOrders(user.id)}
                                        className="w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <History className="w-4 h-4" /> Ver pedidos
                                      </button>
                                      <hr className="my-1" />
                                      <button
                                        onClick={() => { toggleUserStatus(user.id, user.status); setActiveActionsUserId(null); }}
                                        className={`w-full px-4 py-2 text-sm text-left hover:bg-gray-50 flex items-center gap-2 ${user.status === 'active' ? 'text-yellow-600' : 'text-green-600'
                                          }`}
                                      >
                                        {user.status === 'active' ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                                        {user.status === 'active' ? 'Suspender' : 'Activar'}
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>

                          {/* Fila expandida - Carrito */}
                          {isExpanded && (
                            <tr key={`${user.id}-expanded`}>
                              <td colSpan={8} className="px-4 py-4 bg-gray-50 border-t border-b">
                                <div className="max-w-3xl mx-auto">
                                  <div className="flex items-center gap-2 mb-4">
                                    <ShoppingCart className="w-5 h-5 text-gray-600" />
                                    <h3 className="font-medium text-gray-900">Carrito del usuario</h3>
                                  </div>
                                  <UserCartPreview
                                    userId={user.id}
                                    userPriceType={user.price_type || 'publico'}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Paginacion */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t">
                <p className="text-sm text-gray-600">
                  {filteredUsers.length} usuarios en total
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="px-4 py-2 text-sm font-medium bg-blue-50 text-blue-600 rounded-lg">
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Admin Modal */}
      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">Anadir Nuevo Admin</h2>
            <form onSubmit={handleAddAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nombre</label>
                <input
                  type="text"
                  name="name"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  name="email"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Contrasena</label>
                <input
                  type="password"
                  name="password"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Telefono</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Permisos</h3>
                <div className="space-y-2">
                  {Object.entries(adminPermissions).map(([key, value]) => (
                    <label key={key} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setAdminPermissions(prev => ({
                          ...prev,
                          [key]: e.target.checked
                        }))}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-600">
                        {permissionLabels[key as keyof AdminPermissionsForm]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Lista de Precios</label>
                <div className="mt-2 space-y-2">
                  {['contado', 'credito', 'mayorista'].map(pt => (
                    <div key={pt} className="flex items-center">
                      <input
                        type="radio"
                        name="price_type"
                        value={pt}
                        defaultChecked={pt === 'contado'}
                        className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                      />
                      <label className="ml-2 block text-sm text-gray-900 capitalize">
                        {pt}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddAdminModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Procesando...
                    </span>
                  ) : (
                    'Anadir Admin'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage User Modal */}
      {showManageUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Administrar Usuario</h2>
              <button
                onClick={() => setShowManageUserModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b mb-4">
              {[
                { id: 'info', label: 'Informacion' },
                { id: 'permisos', label: 'Permisos' },
                { id: 'precios', label: 'Lista de Precios' }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveManageTab(tab.id as any)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeManageTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-blue-600'
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <form onSubmit={handleEditUser} className="space-y-4">
              {/* INFO TAB */}
              {activeManageTab === 'info' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Nombre</label>
                    <input
                      type="text"
                      name="name"
                      defaultValue={selectedUser.name}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={selectedUser.email}
                      disabled={true}
                      className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">El email no se puede modificar directamente desde esta interfaz</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Empresa</label>
                    <input
                      type="text"
                      name="company"
                      defaultValue={selectedUser.company}
                      required
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Telefono</label>
                    <input
                      type="tel"
                      name="phone"
                      defaultValue={selectedUser.phone}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Rol de Usuario</label>
                    <div className="mt-2 flex gap-4">
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name="is_admin"
                          value="true"
                          defaultChecked={selectedUser.role === 'admin'}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                        />
                        Administrador
                      </label>
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name="is_admin"
                          value="false"
                          defaultChecked={selectedUser.role !== 'admin'}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                        />
                        Usuario Regular
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* PERMISOS TAB */}
              {activeManageTab === 'permisos' && (
                <div className="space-y-2">
                  {Object.entries(editPermissions).map(([key, value]) => (
                    <label key={key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setEditPermissions(prev => ({
                          ...prev,
                          [key]: e.target.checked
                        }))}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                      <span className="ml-2">
                        {permissionLabels[key as keyof AdminPermissionsForm]}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {/* PRECIOS TAB */}
              {activeManageTab === 'precios' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lista de Precios</label>
                  <div className="space-y-2">
                    {[
                      { id: 'contado', label: 'Contado' },
                      { id: 'credito', label: 'Credito' },
                      { id: 'mayorista', label: 'Mayorista' },
                      { id: 'publico', label: 'Publico' }
                    ].map(opt => (
                      <label key={opt.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="price_type"
                          value={opt.id}
                          defaultChecked={selectedUser.price_type === opt.id}
                          className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Botones */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowManageUserModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Guardando...
                    </span>
                  ) : (
                    'Guardar Cambios'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
